import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  CardActions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Engineering as EngineeringIcon,
  Chat as ChatIcon,
  CalendarMonth as CalendarIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';

const features = [
  {
    title: 'WhatsApp Integration',
    description: 'Dedicated business number with intelligent automation for your WhatsApp communications.',
    icon: <ChatIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
  },
  {
    title: 'AI-Powered Responses',
    description: 'Never miss a customer inquiry with 24/7 automated responses and quote generation.',
    icon: <InsightsIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
  },
  {
    title: 'Website Chatbot',
    description: 'Embed our intelligent chatbot directly on your website to capture leads and answer questions.',
    icon: <CalendarIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
  },
  {
    title: 'Complete Control',
    description: 'You decide when to automate and when to take over the conversation.',
    icon: <EngineeringIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
  },
];

function Home() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
          color: 'white',
          pt: 8,
          pb: 6,
          borderRadius: 2,
          mb: 4,
          boxShadow: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h2"
            align="center"
            color="white"
            gutterBottom
            fontWeight="bold"
          >
            FixMatePro
          </Typography>
          <Typography variant="h5" align="center" color="white" paragraph>
            Your Business WhatsApp, Working 24/7
          </Typography>
          <Typography variant="h6" align="center" color="white" paragraph>
            Never Miss a Job Again - Grow Your Business With 24/7 Automation
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={RouterLink}
              to="/waitlist"
              variant="contained"
              color="secondary"
              size="large"
              sx={{ 
                mx: 1, 
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Join Beta Program
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="outlined"
              size="large"
              sx={{ 
                mx: 1, 
                bgcolor: 'rgba(255,255,255,0.1)', 
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)',
                  borderColor: 'white',
                }
              }}
            >
              Sign Up
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <Typography
          component="h2"
          variant="h3"
          align="center"
          color="text.primary"
          gutterBottom
        >
          Our Features
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature) => (
            <Grid item key={feature.title} xs={12} sm={6} md={3}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {feature.icon}
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h3" align="center">
                    {feature.title}
                  </Typography>
                  <Typography align="center">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* WhatsApp Integration Section */}
      <Container sx={{ py: 6 }} maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom color="primary.main" fontWeight="bold">
              Your WhatsApp Business Assistant
            </Typography>
            <Typography variant="body1" paragraph>
              Your customers are already messaging you on WhatsApp. Now you can be there for them 24/7 without being chained to your phone.
            </Typography>
            <Typography variant="body1" paragraph>
              Stop missing customer inquiries and grow your business with automated responses and appointment scheduling.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                Perfect for:
              </Typography>
              <Typography variant="body1">• Plumbers</Typography>
              <Typography variant="body1">• Electricians</Typography>
              <Typography variant="body1">• HVAC Technicians</Typography>
              <Typography variant="body1">• Handymen</Typography>
              <Typography variant="body1">• Any service professional using WhatsApp</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
              <Typography variant="h6" gutterBottom color="primary.main">
                Beta Tester Benefits:
              </Typography>
              <Typography variant="body1">• Free access during beta (3 months)</Typography>
              <Typography variant="body1">• Priority support</Typography>
              <Typography variant="body1">• Shape the future of the product</Typography>
              <Typography variant="body1">• Special pricing after beta</Typography>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="error">
                  Limited Spots Available!
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Only 100 spots for our beta program
                </Typography>
                <Button
                  component={RouterLink}
                  to="/waitlist"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Join the Waitlist
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Website Chatbot Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 6, mt: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'white', 
                borderRadius: 2, 
                boxShadow: 3,
                border: '1px solid #e0e0e0',
                height: '400px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  p: 1.5, 
                  borderTopLeftRadius: 8, 
                  borderTopRightRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold">Chat with Us</Typography>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#ffeb3b', borderRadius: '50%', mx: 0.5 }} />
                    <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: '50%', mx: 0.5 }} />
                  </Box>
                </Box>
                <Box sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      p: 1.5, 
                      borderRadius: '18px', 
                      maxWidth: '80%',
                      ml: 'auto'
                    }}>
                      <Typography variant="body2">Hi, I need a quote for fixing my leaking faucet.</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: 'grey.200', 
                      p: 1.5, 
                      borderRadius: '18px', 
                      maxWidth: '80%' 
                    }}>
                      <Typography variant="body2">Hello! I'd be happy to help you with a quote for your leaking faucet. Could you please provide some details about the issue?</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      p: 1.5, 
                      borderRadius: '18px', 
                      maxWidth: '80%',
                      ml: 'auto'
                    }}>
                      <Typography variant="body2">It's dripping constantly and there's some corrosion around the base.</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex' }}>
                    <Box sx={{ 
                      bgcolor: 'grey.200', 
                      p: 1.5, 
                      borderRadius: '18px', 
                      maxWidth: '80%' 
                    }}>
                      <Typography variant="body2">Based on your description, the estimated cost would be $85-120. Would you like to schedule an appointment?</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex' }}>
                  <Box sx={{ 
                    flexGrow: 1, 
                    mr: 1, 
                    p: 1.5, 
                    borderRadius: 4, 
                    border: '1px solid #e0e0e0',
                    bgcolor: 'background.paper'
                  }}>
                    <Typography variant="body2" color="text.secondary">Type your message...</Typography>
                  </Box>
                  <Button variant="contained" color="primary" sx={{ borderRadius: 2, minWidth: '60px' }}>
                    Send
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom color="primary.main" fontWeight="bold">
                Website Chatbot Integration
              </Typography>
              <Typography variant="body1" paragraph>
                Capture more leads directly from your website with our intelligent chatbot that can be embedded on any page.
              </Typography>
              <Typography variant="body1" paragraph>
                Our chatbot can handle customer inquiries, provide quotes, and schedule appointments - even when you're not available.
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Key Benefits:
                </Typography>
                <Typography variant="body1">• Instant responses to customer inquiries</Typography>
                <Typography variant="body1">• Automated quote generation based on service descriptions</Typography>
                <Typography variant="body1">• Appointment scheduling integration</Typography>
                <Typography variant="body1">• Seamless handoff to human agents when needed</Typography>
                <Typography variant="body1">• Customizable to match your website's branding</Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/waitlist"
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
              >
                Try It In Beta
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          borderRadius: 2,
          mt: 4,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom>
            Ready to grow your business?
          </Typography>
          <Typography variant="h6" align="center" paragraph>
            Join the FixMatePro beta program today and never miss a customer message again.
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              component={RouterLink}
              to="/waitlist"
              variant="contained"
              size="large"
              sx={{ 
                mx: 1, 
                bgcolor: 'white', 
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}
            >
              Join Beta Program
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
